import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getPatients,
  getPatientById,
  createPatient,
  updatePatient,
  archivePatient,
  unarchivePatient,
} from "../../services/patientService";
import { displayDateUS } from "../../utils/dateUtils";
import { STATUS_LABELS } from "./components/StatusBadge";

const VALID_STATUSES = new Set(Object.keys(STATUS_LABELS));

const csvEscape = (value) => {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const usePatients = ({
  enableAdminFilters = false,
  /** Admin-only: when true, fetch archived patients. */
  showArchived = false,
} = {}) => {
  const [searchParams] = useSearchParams();
  const statusFromUrl = enableAdminFilters ? searchParams.get("status") || "" : "";

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: VALID_STATUSES.has(statusFromUrl) ? statusFromUrl : "",
    insurance: "",
    sortBy: "status",
    sortOrder: "asc",
  });

  useEffect(() => {
    if (!VALID_STATUSES.has(statusFromUrl)) return;
    setFilters((f) =>
      f.status === statusFromUrl ? f : { ...f, status: statusFromUrl },
    );
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [statusFromUrl]);

  // Reset to page 1 when switching active ↔ archived.
  useEffect(() => {
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [showArchived]);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPatients({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        ...(enableAdminFilters && filters.status
          ? { status: filters.status }
          : {}),
        ...(enableAdminFilters && filters.insurance
          ? { insurance: filters.insurance }
          : {}),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ...(showArchived ? { archived: true } : {}),
      });

      const result = response.result || response.data || {};
      setPatients(result.patients || []);
      setStatusCounts(result.statusCounts || {});
      const pg = result.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: pg.total ?? result.total ?? 0,
        totalPages: pg.totalPages ?? result.totalPages ?? 1,
      }));
    } catch (err) {
      toast.error(err.message || "Failed to load patients");
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    filters,
    enableAdminFilters,
    showArchived,
  ]);

  useEffect(() => {
    const timer = setTimeout(fetchPatients, filters.search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchPatients, filters.search]);

  const savePatient = async (data, id) => {
    try {
      if (id) {
        await updatePatient(id, data);
        toast.success("Patient updated successfully");
      } else {
        await createPatient(data);
        toast.success("Patient created successfully");
      }
      await fetchPatients();
      return true;
    } catch (err) {
      toast.error(err.message || "Failed to save patient");
      return false;
    }
  };

  const archivePatientById = useCallback(
    async (id) => {
      try {
        await archivePatient(id);
        toast.success("Patient archived successfully");
        await fetchPatients();
        return true;
      } catch (err) {
        toast.error(err.message || "Failed to archive patient");
        return false;
      }
    },
    [fetchPatients],
  );

  const unarchivePatientById = useCallback(
    async (id) => {
      try {
        await unarchivePatient(id);
        toast.success("Patient unarchived successfully");
        await fetchPatients();
        return true;
      } catch (err) {
        toast.error(err.message || "Failed to unarchive patient");
        return false;
      }
    },
    [fetchPatients],
  );

  const exportPatientsCsv = async () => {
    if (!enableAdminFilters) {
      toast.error("You do not have permission to export patients");
      return false;
    }
    try {
      const pageSize = 100;
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await getPatients({
          page,
          limit: pageSize,
          search: filters.search || undefined,
          ...(enableAdminFilters && filters.status
            ? { status: filters.status }
            : {}),
          ...(enableAdminFilters && filters.insurance
            ? { insurance: filters.insurance }
            : {}),
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          ...(showArchived ? { archived: true } : {}),
        });
        const result = response.result || response.data || {};
        const batch = result.patients || [];
        allRows.push(...batch);
        const pg = result.pagination || {};
        totalPages = Math.max(1, Number(pg.totalPages ?? result.totalPages ?? 1));
        page += 1;
      } while (page <= totalPages);

      if (!allRows.length) {
        toast.error("No patients to export");
        return false;
      }

      const headers = [
        "MRN",
        "Patient Name",
        "Phone",
        "City",
        "Insurance",
        "SOC date",
        "Status",
      ];
      const lines = [
        headers.join(","),
        ...allRows.map((p) =>
          [
            p.mrn,
            [p.lastName, p.firstName]
              .map((part) => String(part || "").trim())
              .filter(Boolean)
              .join(", "),
            p.phone,
            p.city,
            p.insurance || "",
            displayDateUS(p.admissionDate) || "",
            STATUS_LABELS[p.status] || p.status || "",
          ]
            .map(csvEscape)
            .join(","),
        ),
      ];
      const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `patients_${showArchived ? "archived" : "active"}_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Exported ${allRows.length} patient(s)`);
      return true;
    } catch (err) {
      toast.error(err.message || "Export failed");
      return false;
    }
  };

  return {
    patients,
    loading,
    pagination,
    filters,
    statusCounts,
    setFilters,
    setPagination,
    fetchPatients,
    savePatient,
    archivePatientById,
    unarchivePatientById,
    exportPatientsCsv,
    getPatientById,
  };
};

export default usePatients;
